import { Link } from "react-router-dom";

import { SocialIcon } from "@/components/SocialIcon";
import { Switch } from "@/components/ui/switch";
import { useProfile } from "../profile/ProfileContext";
import { BuilderOffering, markBuilderReturn } from "./builderReturn";

/**
 * What of the host goes out with this link: their pictures, and their social profiles.
 *
 * <p>The links are set once on the profile and then appear on every page, which is convenient and
 * easy to forget — a host who added a LinkedIn profile months ago has no reason to remember it is
 * public. Showing them here answers "what goes out with this?" while they are deciding.</p>
 *
 * <p>The two switches are <b>per link</b>, and separate. A host may want the company banner without
 * their own face, or a formal link with neither. Both default on, so nothing a host already shares
 * changes.</p>
 *
 * <p>Self-contained rather than a {@code Panel}: each builder defines its own Panel privately, and
 * this has to sit in all three.</p>
 */
export function BuilderSocialNote({
  offering,
  showAvatar,
  showBanner,
  onShowAvatarChange,
  onShowBannerChange,
  disabled,
}: {
  /** Which builder this sits in, so the host can be brought back to it. */
  offering: BuilderOffering;
  showAvatar: boolean;
  showBanner: boolean;
  onShowAvatarChange: (next: boolean) => void;
  onShowBannerChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  const { state } = useProfile();
  if (state.status !== "ready") return null;
  const profile = state.profile;
  const links = profile.socialLinks ?? [];

  return (
    <div className="space-y-3 rounded-xl border border-border bg-muted/20 px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="text-sm text-foreground">
          {links.length > 0 ? "Shown on this page:" : "No links on this page yet"}
        </span>
        {links.map((link) => (
          <span
            key={link.network}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground"
          >
            <SocialIcon network={link.network} className="h-4 w-4" />
            {link.label}
          </span>
        ))}
        {/*
          markBuilderReturn before leaving, exactly as the calendar-connect nudge does. The builders
          are local state inside BookingPagesPage rather than routes, so navigating away drops the
          host back on the All-links list with no way back to the half-built link they were on. The
          draft itself survives; where they were does not, unless it is written down here.
        */}
        <Link
          to="/profile#social-links"
          onClick={() => markBuilderReturn(offering)}
          className="ml-auto text-sm font-semibold text-brand-blue hover:underline"
        >
          {links.length > 0 ? "Change" : "Add your links"}
        </Link>
      </div>

      {/* Both switches are always offered, including with no picture uploaded.

          They used to be hidden unless the matching image existed, on the reasoning that a switch
          for a picture that does not exist has no effect. It does have one: the avatar falls back
          to the host's initials, so "show my profile picture" off and on are visibly different
          pages either way.

          Hiding them was also how a link could sit with a switch off and no way for the host to
          see it. That is not hypothetical — a live link had its avatar off for days while its
          owner looked at the upload for the cause. A control whose state matters must be visible
          in the state that matters. */}
      <div className="space-y-2 border-t border-border pt-3">
        <label className="flex items-center justify-between gap-3 text-sm">
          <span>
            Show my profile picture
            {!profile.avatarUrl && (
              <span className="ml-2 text-xs text-muted-foreground">your initials, until you add one</span>
            )}
          </span>
          <Switch checked={showAvatar} disabled={disabled} onCheckedChange={onShowAvatarChange} />
        </label>
        {/* The banner has no fallback: with none uploaded the header keeps its gradient either
            way, so this one really is inert without an image and says so. */}
        <label className="flex items-center justify-between gap-3 text-sm">
          <span>
            Show my banner
            {!profile.bannerUrl && (
              <span className="ml-2 text-xs text-muted-foreground">nothing to show yet</span>
            )}
          </span>
          <Switch checked={showBanner} disabled={disabled} onCheckedChange={onShowBannerChange} />
        </label>
      </div>
    </div>
  );
}
